import React, {useContext, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';

import '../styles/NavPanel.css'
import {Context} from "../index";
import {setCookie} from "../utils/cookies";
import {URLS} from "../utils/urls";
import {observer} from "mobx-react-lite";

const logo = require('../assets/logo/logo.png');

const NAV_EXPANDED_STORAGE_KEY = 'navPanelExpanded';

// Icon set: inline SVG (stroke, currentColor) instead of the old PNG
// active/inactive pairs — recolors for hover/active state for free and
// stays crisp at any pixel density.
const ICONS: { [key: string]: JSX.Element } = {
    home: <path d="M3 11.5 12 4l9 7.5M5.5 10v9a1 1 0 0 0 1 1H9a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h2.5a1 1 0 0 0 1-1v-9"/>,
    companies: <><rect x="4" y="3" width="10" height="18" rx="1"/><rect x="14" y="9" width="6" height="12" rx="1"/><line x1="7" y1="7" x2="7.01" y2="7"/><line x1="10.5" y1="7" x2="10.51" y2="7"/><line x1="7" y1="11" x2="7.01" y2="11"/><line x1="10.5" y1="11" x2="10.51" y2="11"/><line x1="7" y1="15" x2="7.01" y2="15"/><line x1="10.5" y1="15" x2="10.51" y2="15"/></>,
    suppliers: <><rect x="2" y="7" width="12" height="9" rx="1"/><path d="M14 10h3l3 3v3h-6z"/><circle cx="6.5" cy="18" r="1.6"/><circle cx="16.5" cy="18" r="1.6"/></>,
    admin: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/>,
    logout: <><path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3"/><path d="M15 16l4-4-4-4"/><path d="M19 12H9"/></>,
    // Points right at rest ("click to expand"); flipped via CSS to point
    // left once the panel is expanded ("click to collapse") — same
    // scaleX(-1) idiom as CalculationsContainer's popup-panel reveal tab.
    chevron: <path d="M9 6l6 6-6 6"/>,
};

function Icon(props: { name: string, size?: number }) {
    return (
        <svg width={props.size ?? 18} height={props.size ?? 18} viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
            {ICONS[props.name]}
        </svg>
    );
}

function NavItem(props: { href: string, icon: string, label: string, isActive: boolean }) {
    return (
        <Link to={props.href}
              className={`navPanel__item navPanel__item--${props.icon} ${props.isActive ? 'is-active' : ''}`}>
            <span className='navPanel__item-icon'><Icon name={props.icon}/></span>
            <span className='navPanel__item-label'>{props.label}</span>
        </Link>
    );
}

const NavPanel = observer(() => {
    const { user } = useContext(Context)
    const location = useLocation();

    const userRole: 'USER' | 'SUPERUSER' | 'ACCOUNTANT' = user.getUser.role;
    const username: string = user.getUser.username ?? '';
    const initials = username.slice(0, 2).toUpperCase();
    // Бухгалтер only ever has "/companies" — no dashboard, suppliers,
    // admin, or profile page to link to.
    const isAccountant = userRole === 'ACCOUNTANT';

    // Expansion used to be a pure CSS :hover effect; now it's a click-to-pin
    // toggle instead, remembered per browser so it doesn't reset every visit.
    const [isExpanded, setIsExpanded] = useState<boolean>(() => {
        try {
            return localStorage.getItem(NAV_EXPANDED_STORAGE_KEY) === '1';
        } catch {
            return false;
        }
    });

    function toggleExpanded() {
        setIsExpanded((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(NAV_EXPANDED_STORAGE_KEY, next ? '1' : '0');
            } catch {
                // localStorage can throw (private mode, blocked site data) --
                // the toggle still works for the current page, just won't
                // be remembered next visit.
            }
            return next;
        });
    }

    function logout() {
        setCookie('username', '', -365);
        setCookie('password', '', -365);
        user.setIsAuth(false);
        user.setUser({});
        localStorage.removeItem('token');
    }

    return (
        <div className={`navPanel-wrapper ${isExpanded ? 'is-expanded' : ''}`}>
            <div className='navIndent'></div>
            <nav className='navPanel'>

                <div className='navPanel__toggle-row'>
                    <button className='navPanel__toggle-btn' onClick={toggleExpanded}
                            title={isExpanded ? 'Свернуть панель' : 'Развернуть панель'}
                            aria-label={isExpanded ? 'Свернуть панель' : 'Развернуть панель'}>
                        <Icon name='chevron' size={14}/>
                    </button>
                </div>

                <div className='navPanel__brand'>
                    <span className='navPanel__brand-mark'><img src={logo} className='navPanel__brand-logo' alt='логотип'/></span>
                    <span className='navPanel__brand-name'>CRM</span>
                </div>

                <div className='navPanel__nav'>
                    {
                        !isAccountant &&
                            <NavItem href={URLS.MAIN_URL} icon='home' label='Главная'
                                     isActive={location.pathname === URLS.MAIN_URL}/>
                    }
                    <NavItem href={URLS.COMPANIES_URL} icon='companies' label='Предприятия'
                             isActive={location.pathname.startsWith(URLS.COMPANIES_URL) || location.pathname.startsWith('/company/')}/>
                    {
                        !isAccountant &&
                            <NavItem href={URLS.SUPPLIERS_URL} icon='suppliers' label='Поставщики'
                                     isActive={location.pathname.startsWith(URLS.SUPPLIERS_URL) || location.pathname.startsWith('/supplier/')}/>
                    }
                    {
                        userRole === 'SUPERUSER' ?
                            <NavItem href={URLS.ADMIN_URL} icon='admin' label='Администрирование'
                                     isActive={location.pathname.startsWith(URLS.ADMIN_URL)}/>
                            :
                            null
                    }
                </div>

                <div className='navPanel__spacer'></div>

                <div className='navPanel__footer'>
                    {
                        isAccountant ?
                            <span className='avatar' title={username} aria-label={username}>{initials}</span>
                            :
                            <Link to={URLS.PROFILE_URL} className={`avatar ${location.pathname === URLS.PROFILE_URL ? 'is-active' : ''}`}
                                  title='Профиль' aria-label='Профиль'>{initials}</Link>
                    }
                    <span className='navPanel__user-name'>{username}</span>
                    <button className='navPanel__logout-btn' onClick={logout} title='Выйти' aria-label='Выйти'>
                        <Icon name='logout' size={17}/>
                    </button>
                </div>

            </nav>
        </div>
    );
});


export default NavPanel;
